import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    kindLabel, surveyTypeLabel, responseTypeLabel, SurveyTypes, MediaTypes, ResponseTypes,
    ALL_MEDIA_TYPES
} from '@aiinterview/shared';
import { api, ApiError } from '../api/client';
import {
    Button, Card, Field, TextInput, TextArea, Select, Checkbox, Loading, ErrorNote,
    confirmAction, toast
} from '../components/ui';
import { DataTable } from '../components/DataTable';

/**
 * Survey formats and listening formats.
 *
 * One component for both. They are the same thing with a different `kind`, and two copies of this
 * would be two places to fix every future change to it.
 */
interface Format {
    id: number;
    name: string;
    surveyType: string;
    mediaType: string | null;
    description: string | null;
    introText: string | null;
    defaultMaxAnswerSeconds: number;
    language: string;
    publicSlug: string | null;
    isOpen: boolean;
    opensAt: string | null;
    closesAt: string | null;
    questionCount?: number;
    responseCount?: number;
    questionIds?: number[];
}

const EMPTY = {
    id: undefined as number | undefined,
    surveyType: SurveyTypes.General as string,
    mediaType: MediaTypes.Audio as string,
    name: '', description: '', introText: '',
    defaultMaxAnswerSeconds: '120', language: 'en-IN',
    isOpen: false, hasSharedLink: true,
    opensAt: '', closesAt: '',
    questionIds: [] as number[]
};

/** datetime-local wants "YYYY-MM-DDTHH:mm" and gives nothing useful for anything else. */
const toLocalInput = (iso: string | null): string =>
    iso ? new Date(iso).toISOString().slice(0, 16) : '';

export function InteractionFormats({ kind }: { kind: 'Survey' | 'Listening' }) {
    const [form, setForm] = useState(EMPTY);
    const [error, setError] = useState<ApiError | null>(null);
    const queryClient = useQueryClient();

    const noun = kindLabel(kind).toLowerCase();

    // A listening session is a spoken conversation by definition, and the employee picks audio or
    // video for themselves - so neither control belongs on it.
    const isListening = kind === 'Listening';
    const isForm = !isListening && form.surveyType === SurveyTypes.Form;

    const formats = useQuery({
        queryKey: ['interaction-formats', kind],
        queryFn: () => api.get<Format[]>(`/api/admin/interaction-formats?kind=${kind}`)
    });

    const questions = useQuery({
        queryKey: ['questions', kind],
        queryFn: () => api.get<{
            id: number; questionText: string; responseType: string; optionsJson: string;
        }[]>(`/api/admin/questions?kind=${kind}`)
    });

    const save = useMutation({
        mutationFn: () => api.post('/api/admin/interaction-formats', {
            ...form,
            kind,
            // The server forces both of these for a listening session, but sending what the screen
            // actually showed keeps the payload honest rather than relying on it.
            surveyType: isListening ? SurveyTypes.General : form.surveyType,
            mediaType: isForm || isListening ? null : form.mediaType,
            defaultMaxAnswerSeconds: Number(form.defaultMaxAnswerSeconds),
            opensAt: form.opensAt ? new Date(form.opensAt).toISOString() : null,
            closesAt: form.closesAt ? new Date(form.closesAt).toISOString() : null
        }),
        onSuccess: async () => {
            toast('Saved.');
            setForm(EMPTY);
            setError(null);
            await queryClient.invalidateQueries({ queryKey: ['interaction-formats', kind] });
        },
        onError: (caught: ApiError) => setError(caught)
    });

    const remove = useMutation({
        mutationFn: (id: number) => api.post(`/api/admin/interaction-formats/${id}/delete`),
        onSuccess: async () => {
            toast('Removed.');
            await queryClient.invalidateQueries({ queryKey: ['interaction-formats', kind] });
        },
        onError: (caught: Error) => toast(caught.message, 'error')
    });

    const load = async (id: number) => {
        const full = await api.get<Format>(`/api/admin/interaction-formats/${id}`);
        setForm({
            id: full.id,
            surveyType: full.surveyType,
            mediaType: full.mediaType ?? MediaTypes.Audio,
            name: full.name,
            description: full.description ?? '',
            introText: full.introText ?? '',
            defaultMaxAnswerSeconds: String(full.defaultMaxAnswerSeconds),
            language: full.language,
            isOpen: full.isOpen,
            hasSharedLink: full.publicSlug !== null,
            opensAt: toLocalInput(full.opensAt),
            closesAt: toLocalInput(full.closesAt),
            questionIds: full.questionIds ?? []
        });
    };

    const toggleQuestion = (id: number) =>
        setForm((f) => ({
            ...f,
            questionIds: f.questionIds.includes(id)
                ? f.questionIds.filter((v) => v !== id)
                : [...f.questionIds, id]
        }));

    const shareUrl = (slug: string) => `${window.location.origin}/interact/new/${slug}`;

    return (
        <div className="space-y-6">
            <Card
                heading
                title={`${kindLabel(kind)} formats`}
                lede={`A format is the set of questions a ${noun} asks, and when it is open. `
                    + (isListening
                        ? 'Employees answer out loud; nothing here is scored.'
                        : 'A questionnaire asks each question its own way; a spoken conversation '
                          + 'reads them all aloud. Nothing here is scored.')}
            >
                {formats.isLoading ? <Loading /> : (
                    <DataTable
                        rows={formats.data ?? []}
                        rowKey={(row) => row.id}
                        empty={`No ${noun} formats yet.`}
                        columns={[
                            {
                                header: 'Name',
                                sortBy: (row) => row.name,
                                cell: (row) => <span className="font-medium">{row.name}</span>
                            },
                            ...(isListening ? [] : [{
                                header: 'Type',
                                sortBy: (row: Format) => row.surveyType,
                                cell: (row: Format) => (
                                    <span>
                                        {surveyTypeLabel(row.surveyType)}
                                        {row.mediaType && (
                                            <span className="ml-2 text-xs text-slate-500">
                                                {responseTypeLabel(row.mediaType).toLowerCase()}
                                            </span>
                                        )}
                                    </span>
                                )
                            }]),
                            {
                                header: 'Questions',
                                sortBy: (row) => row.questionCount ?? 0,
                                cell: (row) => row.questionCount ?? 0
                            },
                            {
                                header: 'Responses',
                                sortBy: (row) => row.responseCount ?? 0,
                                cell: (row) => row.responseCount ?? 0
                            },
                            {
                                header: 'Open',
                                cell: (row) => row.isOpen ? 'Yes' : 'No'
                            },
                            {
                                header: 'Shared link',
                                cell: (row) => row.publicSlug
                                    ? (
                                        <Button
                                            variant="secondary"
                                            onClick={async () => {
                                                await navigator.clipboard.writeText(
                                                    shareUrl(row.publicSlug!));
                                                toast('Link copied.');
                                            }}
                                        >
                                            Copy
                                        </Button>
                                    )
                                    : '—'
                            },
                            {
                                header: '',
                                className: 'text-right',
                                cell: (row) => (
                                    <div className="flex justify-end gap-2">
                                        <Button variant="secondary" onClick={() => load(row.id)}>
                                            Edit
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            onClick={async () => {
                                                const confirmed = await confirmAction({
                                                    title: `Remove ${row.name}?`,
                                                    confirmText: 'Remove',
                                                    danger: true
                                                });
                                                if (confirmed) remove.mutate(row.id);
                                            }}
                                        >
                                            Remove
                                        </Button>
                                    </div>
                                )
                            }
                        ]}
                    />
                )}
            </Card>

            <Card title={form.id ? `Edit ${form.name}` : `Add a ${noun}`}>
                <form className="space-y-5" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
                    {error && !error.errors && <ErrorNote message={error.message} />}

                    {isListening ? (
                        <p className="text-sm" style={{ color: 'var(--text-muted-safe)' }}>
                            A listening session is always answered out loud. The employee chooses
                            whether that is audio or on camera when they start.
                        </p>
                    ) : (
                        <div className="grid gap-4 sm:grid-cols-2">
                            <Field
                                label="Type"
                                hint="A questionnaire asks each question its own way. A spoken
                                      conversation reads every question aloud."
                            >
                                <Select
                                    value={form.surveyType}
                                    onChange={(e) =>
                                        setForm((f) => ({ ...f, surveyType: e.target.value }))}
                                >
                                    {[SurveyTypes.Form, SurveyTypes.General].map((type) => (
                                        <option key={type} value={type}>
                                            {surveyTypeLabel(type)}
                                        </option>
                                    ))}
                                </Select>
                            </Field>

                            {!isForm && (
                                <Field
                                    label="Answers are"
                                    error={error?.fieldError('mediaType')}
                                    hint="Every answer in this survey is captured this way."
                                >
                                    <Select
                                        value={form.mediaType}
                                        onChange={(e) =>
                                            setForm((f) => ({ ...f, mediaType: e.target.value }))}
                                        invalid={!!error?.fieldError('mediaType')}
                                    >
                                        {ALL_MEDIA_TYPES.map((type) => (
                                            <option key={type} value={type}>
                                                {responseTypeLabel(type)}
                                            </option>
                                        ))}
                                    </Select>
                                </Field>
                            )}
                        </div>
                    )}

                    <div className="grid gap-4 sm:grid-cols-2">
                        <Field label="Name" error={error?.fieldError('name')}>
                            <TextInput
                                value={form.name}
                                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                                invalid={!!error?.fieldError('name')}
                            />
                        </Field>

                        <Field label="Seconds per answer">
                            <TextInput
                                type="number" min="10"
                                value={form.defaultMaxAnswerSeconds}
                                onChange={(e) =>
                                    setForm((f) => ({ ...f, defaultMaxAnswerSeconds: e.target.value }))}
                            />
                        </Field>
                    </div>

                    <Field label="Description" hint="Shown on the list employees see.">
                        <TextArea
                            value={form.description}
                            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                        />
                    </Field>

                    <Field
                        label="What to say before they start"
                        hint="Shown above the form asking for their employee code."
                    >
                        <TextArea
                            value={form.introText}
                            onChange={(e) => setForm((f) => ({ ...f, introText: e.target.value }))}
                        />
                    </Field>

                    <div className="grid gap-4 sm:grid-cols-2">
                        <Field label="Opens" hint="Leave blank to open as soon as it is switched on.">
                            <TextInput
                                type="datetime-local"
                                value={form.opensAt}
                                onChange={(e) => setForm((f) => ({ ...f, opensAt: e.target.value }))}
                            />
                        </Field>

                        <Field
                            label="Closes"
                            error={error?.fieldError('closesAt')}
                            hint="Leave blank to stay open until you switch it off."
                        >
                            <TextInput
                                type="datetime-local"
                                value={form.closesAt}
                                onChange={(e) => setForm((f) => ({ ...f, closesAt: e.target.value }))}
                                invalid={!!error?.fieldError('closesAt')}
                            />
                        </Field>
                    </div>

                    <div className="space-y-2.5">
                        <Checkbox
                            label="Open for responses"
                            hint="Nobody can answer until this is ticked, whatever the dates say."
                            checked={form.isOpen}
                            onChange={(checked) => setForm((f) => ({ ...f, isOpen: checked }))}
                        />
                        <Checkbox
                            label="Give this a shared link"
                            hint="A link anyone can open, listed on the employee page. Untick to make
                                  it reachable only through links you issue by hand — the published
                                  URL stops working."
                            checked={form.hasSharedLink}
                            onChange={(checked) => setForm((f) => ({ ...f, hasSharedLink: checked }))}
                        />
                    </div>

                    <Field
                        label="Questions to ask, in order"
                        error={error?.fieldError('questionIds')}
                        hint={isForm
                            ? `From the ${noun} section of the bank. Each one is answered the way it `
                              + 'says beneath it.'
                            : `From the ${noun} section of the bank. Every one is read aloud.`}
                    >
                        <div className="max-h-64 space-y-1.5 overflow-y-auto rounded-lg border border-slate-200 p-3">
                            {(questions.data ?? []).length === 0
                                ? (
                                    <p className="text-sm text-slate-500">
                                        There are no {noun} questions yet. Add some under
                                        Setup → Questions, with the section set to {kindLabel(kind)}.
                                    </p>
                                )
                                : (questions.data ?? []).map((q) => (
                                    <Checkbox
                                        key={q.id}
                                        label={q.questionText}
                                        // Shown only for a questionnaire, because that is the only
                                        // place a question's own response type is used. On a spoken
                                        // survey it would say something untrue about what happens.
                                        hint={isForm ? responseTypeLabel(q.responseType) : undefined}
                                        checked={form.questionIds.includes(q.id)}
                                        onChange={() => toggleQuestion(q.id)}
                                    />
                                ))}
                        </div>
                    </Field>

                    <div className="flex gap-2">
                        <Button type="submit" busy={save.isPending}>Save</Button>
                        {form.id && (
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={() => { setForm(EMPTY); setError(null); }}
                            >
                                Cancel
                            </Button>
                        )}
                    </div>
                </form>
            </Card>
        </div>
    );
}
